import { useEffect, useMemo, useRef, useState } from "react";

type Props = {
  src: string;
  poster?: string;
  /**
   * Real description of what the poster frame shows. When provided the poster
   * is exposed to assistive tech and image search instead of being decorative.
   */
  posterAlt?: string;
  videoClassName?: string;
  playbackRate?: number;
  videoStyle?: React.CSSProperties;
  /** If true, do not load the <video> at all — just show the poster. */
  posterOnly?: boolean;
  /**
   * If true, mount and preload the video immediately (use for the first
   * above-the-fold hero on a page). Default false: video is lazy-mounted
   * when it scrolls near the viewport.
   */
  priority?: boolean;
};

/**
 * Smooth, always-playing hero video background.
 * - Poster paints instantly (no flash, no layout shift).
 * - Video plays on every device (including phones), muted + inline + looped.
 * - Aggressively re-attempts play() so iOS Safari / Low Power Mode don't
 *   leave it paused after tab switches, gestures, or background returns.
 * - Only opt-out is prefers-reduced-motion (or explicit posterOnly).
 */
export const VideoHeroBackground = ({
  src,
  poster,
  posterAlt,
  videoClassName = "",
  playbackRate,
  videoStyle,
  posterOnly = false,
  priority = false,
}: Props) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLVideoElement>(null);
  const visibleRef = useRef(true);
  const offscreenSinceRef = useRef<number | null>(null);
  const [ready, setReady] = useState(false);
  const [mounted, setMounted] = useState(priority);

  const reduceMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    try { return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false; } catch { return false; }
  }, []);

  const skipVideo = posterOnly || reduceMotion;
  const eagerSrc = useMemo(() => (src.includes("#") ? src : `${src}#t=0.001`), [src]);

  // Lazy-mount the <video> once the wrapper approaches the viewport (non-priority only).
  useEffect(() => {
    if (skipVideo || mounted) return;
    const el = wrapRef.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) { setMounted(true); return; }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { setMounted(true); io.disconnect(); break; }
        }
      },
      { rootMargin: "400px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [skipVideo, mounted]);

  // Visibility: pause off-screen, resume on-screen. Unmount after sustained
  // off-screen time on non-priority heroes to free decoders on mobile.
  useEffect(() => {
    if (skipVideo || !mounted) return;
    const el = wrapRef.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) return;
    let unmountTimer: number | null = null;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          visibleRef.current = e.isIntersecting;
          const v = ref.current;
          if (e.isIntersecting) {
            offscreenSinceRef.current = null;
            if (unmountTimer) { clearTimeout(unmountTimer); unmountTimer = null; }
            v?.play().catch(() => {});
          } else {
            try { v?.pause(); } catch {}
            offscreenSinceRef.current = Date.now();
            if (!priority && !unmountTimer) {
              unmountTimer = window.setTimeout(() => {
                if (!visibleRef.current) setMounted(false);
              }, 4000);
            }
          }
        }
      },
      { rootMargin: "300px 0px" }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (unmountTimer) clearTimeout(unmountTimer);
    };
  }, [skipVideo, mounted, priority]);

  // Drive playback + auto-resume on every interruption.
  useEffect(() => {
    if (skipVideo || !mounted) return;
    const v = ref.current;
    if (!v) return;
    if (typeof playbackRate === "number") v.playbackRate = playbackRate;
    if (v.readyState >= 2) setReady(true);

    let gestureBound = true;
    const isPlayingOk = () => !v.paused && !v.ended && v.readyState >= 3;

    const tryPlay = () => {
      if (!visibleRef.current) return;
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          if (isPlayingOk() && gestureBound) unbindGestures();
        }).catch(() => {});
      }
    };
    const onReady = () => { setReady(true); tryPlay(); };
    const onPlaying = () => { setReady(true); if (gestureBound && isPlayingOk()) unbindGestures(); };
    const onPause = () => { if (!v.ended && visibleRef.current) setTimeout(tryPlay, 50); };
    const onEnded = () => { try { v.currentTime = 0; } catch {} tryPlay(); };
    const onVis = () => { if (!document.hidden) tryPlay(); };
    const onGesture = () => tryPlay();

    const bindGestures = () => {
      document.addEventListener("pointerdown", onGesture, { passive: true });
      document.addEventListener("touchend", onGesture, { passive: true });
      document.addEventListener("click", onGesture, { passive: true } as any);
      document.addEventListener("keydown", onGesture);
    };
    const unbindGestures = () => {
      gestureBound = false;
      document.removeEventListener("pointerdown", onGesture);
      document.removeEventListener("touchend", onGesture);
      document.removeEventListener("click", onGesture as any);
      document.removeEventListener("keydown", onGesture);
    };

    v.addEventListener("canplay", onReady);
    v.addEventListener("loadeddata", onReady);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onEnded);
    document.addEventListener("visibilitychange", onVis);
    bindGestures();
    tryPlay();

    return () => {
      v.removeEventListener("canplay", onReady);
      v.removeEventListener("loadeddata", onReady);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onEnded);
      document.removeEventListener("visibilitychange", onVis);
      unbindGestures();
    };
  }, [skipVideo, playbackRate, eagerSrc, mounted]);

  return (
    <div
      ref={wrapRef}
      aria-hidden={posterAlt ? undefined : true}
      className="absolute inset-0 overflow-hidden bg-charcoal"
    >
      {poster && (
        <img width={1200} height={800}
          src={poster}
          alt={posterAlt ?? ""}
          className="absolute inset-0 h-full w-full object-cover"
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          {...({ fetchpriority: priority ? "high" : "low" } as any)}
        />
      )}

      {!skipVideo && mounted && (
        <video
          ref={ref}
          src={eagerSrc}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          controls={false}
          disablePictureInPicture
          disableRemotePlayback
          {...({ "webkit-playsinline": "true", "x-webkit-airplay": "deny" } as any)}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
            ready ? "opacity-100" : "opacity-0"
          } ${videoClassName}`}
          style={videoStyle}
        />
      )}
    </div>
  );
};

export default VideoHeroBackground;
